import React, { useState } from 'react';
import { cn } from '@sglara/cn';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { InteractiveList } from '../../InteractiveList/InteractiveList';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Input } from '../../Input';
import { FormLabel } from '../../FormLabel';
import { client } from '../../../api';
import * as Sentry from '@sentry/react';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { AddressAutocomplete, type ParsedAddress } from '../../AddressAutocomplete';
import { useGeocode } from '../../AddressAutocomplete/useGeocode';
import { formatTin, requiredFieldRule } from '../../../utils';
import EditIcon from 'design-system/src/assets/icons/edit.svg?react';
import LocationPinIcon from 'design-system/src/assets/icons/locationPin.svg?react';
import { DropdownWithPlaceholder, DropdownItem } from '../../Dropdown';
import { STATES } from 'design-system/src/sharedConstants';

type OfficeDetail = {
  id: string;
  name: string;
  address: string;
  tin: string | null;
};

type AddOfficeModalProps = {
  open: boolean;
  onClose: () => void;
};

type FormData = {
  name: string;
  tin: string;
  address_line_2: string;
  // Address fields for search mode
  formatted_address: string;
  latitude: number | null;
  longitude: number | null;
  // Address fields for manual mode
  address_line_1: string;
  city: string;
  state: string;
  zipcode: string;
};

export type AddressSearchSectionProps = {
  addressInputValue: string;
  addressError: string | null;
  onAddressInputChange: (val: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  onManualEntry: () => void;
};

export const AddressSearchSection = ({
  addressInputValue,
  addressError,
  onAddressInputChange,
  onAddressSelect,
  onManualEntry,
}: AddressSearchSectionProps) => (
  <div className="flex flex-col">
    <div className="mb-1 flex w-full items-center justify-between [&_label]:mb-0">
      <FormLabel htmlFor="address-input">Address</FormLabel>
      <button
        type="button"
        onClick={onManualEntry}
        className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 hover:opacity-80"
      >
        <EditIcon width={14} height={14} className="text-purple-700" />
        <Typography styledAs="bodyExtraSmallSpaceMedium" color="var(--purple-700)">
          Enter address manually
        </Typography>
      </button>
    </div>
    <AddressAutocomplete
      label="Address"
      hideLabel
      placeholder="Search address"
      value={addressInputValue}
      onChange={onAddressInputChange}
      onAddressSelect={onAddressSelect}
    />
    {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
  </div>
);

export type ManualAddressFieldsProps = {
  control: ReturnType<typeof useForm<FormData>>['control'];
  errors: ReturnType<typeof useForm<FormData>>['formState']['errors'];
  onAddressLookup: () => void;
};

export const ManualAddressFields = ({ control, errors, onAddressLookup }: ManualAddressFieldsProps) => (
  <>
    <div className="flex flex-col">
      <div className="mb-1 flex w-full items-center justify-between [&_label]:mb-0">
        <FormLabel htmlFor="address_line_1">Street address</FormLabel>
        <button
          type="button"
          onClick={onAddressLookup}
          className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-purple-700 hover:text-purple-800"
        >
          <LocationPinIcon width={14} height={14} className="text-current" />
          <Typography styledAs="bodyExtraSmallSpaceMedium" color="currentColor">
            Use address lookup
          </Typography>
        </button>
      </div>
      <Controller
        control={control}
        name="address_line_1"
        shouldUnregister
        rules={{ required: requiredFieldRule('Address') }}
        render={({ field }) => (
          <Input
            label="Street address"
            hideLabel
            placeholder="Street address"
            hasError={!!errors.address_line_1}
            {...field}
          />
        )}
      />
      {errors.address_line_1 && <ErrorMessage>{errors.address_line_1.message}</ErrorMessage>}
    </div>

    <Controller
      control={control}
      name="address_line_2"
      render={({ field }) => (
        <Input
          label={
            <span>
              Address line 2 <span className="text-grayscale-500">Optional</span>
            </span>
          }
          placeholder="Apt / Suite / Floor"
          {...field}
        />
      )}
    />

    <div className="flex gap-4">
      <div className="flex-1">
        <Controller
          control={control}
          name="city"
          shouldUnregister
          rules={{ required: requiredFieldRule('City') }}
          render={({ field }) => <Input label="City" placeholder="City" hasError={!!errors.city} {...field} />}
        />
        {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
      </div>
      <div className="flex w-[120px] flex-col">
        <FormLabel htmlFor="state">State</FormLabel>
        <Controller
          control={control}
          name="state"
          shouldUnregister
          rules={{ required: requiredFieldRule('State') }}
          render={({ field }) => (
            <DropdownWithPlaceholder
              id="state"
              placeholder="State"
              value={field.value}
              onChange={field.onChange}
              $hasError={!!errors.state}
            >
              {Object.keys(STATES).map((abbr) => (
                <DropdownItem key={abbr} value={abbr}>
                  <Typography styledAs="bodySmallDMSans">{abbr}</Typography>
                </DropdownItem>
              ))}
            </DropdownWithPlaceholder>
          )}
        />
        {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
      </div>
      <div className="w-[100px]">
        <Controller
          control={control}
          name="zipcode"
          shouldUnregister
          rules={{ required: requiredFieldRule('Zip') }}
          render={({ field }) => <Input label="Zip" placeholder="Zip" hasError={!!errors.zipcode} {...field} />}
        />
        {errors.zipcode && <ErrorMessage>{errors.zipcode.message}</ErrorMessage>}
      </div>
    </div>
  </>
);

const AddOfficeModal: React.FC<AddOfficeModalProps> = ({ open, onClose }) => {
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const { geocode } = useGeocode();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      tin: '',
      address_line_2: '',
      formatted_address: '',
      latitude: null,
      longitude: null,
      address_line_1: '',
      city: '',
      state: '',
      zipcode: '',
    },
  });

  const { data: officesData, isLoading: isLoadingOffices } = useQuery<{ offices: OfficeDetail[] }>({
    queryKey: ['offices-v2'],
    queryFn: () => client.get('/settings/offices/v2').then((res) => res.data),
    enabled: open,
  });

  const offices = officesData?.offices ?? [];

  const { mutate: createOffice, isPending: isCreating } = useMutation({
    mutationFn: (data: { name: string; tin: string | null; address: string; latitude: number; longitude: number }) =>
      client.post('/settings/offices', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
      toast.custom(() => <Toast variant="success">Office added</Toast>);
      resetForm();
    },
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.custom(() => <Toast variant="error">An office with this address already exists</Toast>);
      } else {
        Sentry.captureException(error);
        toast.custom(() => <StandardErrorToast />);
      }
    },
  });

  const { mutate: deleteOffice } = useMutation({
    mutationFn: (officeId: string) => client.delete(`/settings/offices/${officeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
      toast.custom(() => <Toast variant="success">Office removed</Toast>);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const resetForm = () => {
    reset();
    setIsManualEntry(false);
    setAddressInputValue('');
    setAddressError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    setValue('formatted_address', address.formatted_address);
    setValue('latitude', address.latitude ?? null);
    setValue('longitude', address.longitude ?? null);
    setValue('address_line_1', address.address_line_1);
    setValue('address_line_2', address.address_line_2);
    setValue('city', address.city);
    setValue('state', address.state);
    setValue('zipcode', address.zipcode);
    setAddressInputValue(address.formatted_address);
    setAddressError(null);
  };

  const onSubmit = async (data: FormData) => {
    let address = data.formatted_address;
    let lat = data.latitude;
    let lng = data.longitude;

    if (isManualEntry) {
      address = `${data.address_line_1}, ${data.city}, ${data.state} ${data.zipcode}`;
      const parsed = await geocode(address);
      if (parsed) {
        lat = parsed.latitude ?? null;
        lng = parsed.longitude ?? null;
        address = parsed.formatted_address;
      } else {
        setAddressError('Could not verify address. Please try searching for it instead.');
        return;
      }
    }

    const trimmedAddress = address.trim();

    if (!trimmedAddress) {
      setAddressError('Please select an address.');
      return;
    }

    if (lat == null || lng == null) {
      setAddressError('Could not determine address location. Please try again.');
      return;
    }

    const addressLine2 = data.address_line_2.trim();
    const fullAddress = addressLine2 ? `${trimmedAddress}, ${addressLine2}` : trimmedAddress;

    createOffice({
      name: data.name,
      tin: data.tin || null,
      address: fullAddress,
      latitude: lat,
      longitude: lng,
    });
  };

  const watchedName = watch('name');
  const watchedFormattedAddress = watch('formatted_address');
  const watchedAddressLine1 = watch('address_line_1');
  const watchedCity = watch('city');
  const watchedState = watch('state');
  const watchedZipcode = watch('zipcode');

  const hasRequiredFields = isManualEntry
    ? watchedName?.trim() &&
      watchedAddressLine1?.trim() &&
      watchedCity?.trim() &&
      watchedState?.trim() &&
      watchedZipcode?.trim()
    : watchedName?.trim() && watchedFormattedAddress?.trim();

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add offices</ModalTitle>
      <ModalBody>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-3 rounded-md border border-solid border-border-gray p-3"
        >
          <Controller
            control={control}
            name="name"
            rules={{ required: requiredFieldRule('Office name') }}
            render={({ field }) => (
              <Input label="Office name" placeholder="e.g. Downtown office" hasError={!!errors.name} {...field} />
            )}
          />
          {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

          <Controller
            control={control}
            name="tin"
            render={({ field }) => (
              <Input
                label="TIN"
                placeholder="987-65-4321"
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(formatTin(e.target.value))}
              />
            )}
          />

          {isManualEntry ? (
            <>
              <ManualAddressFields control={control} errors={errors} onAddressLookup={() => setIsManualEntry(false)} />
              {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
            </>
          ) : (
            <>
              <AddressSearchSection
                addressInputValue={addressInputValue}
                addressError={addressError}
                onAddressInputChange={(val) => {
                  setAddressInputValue(val);
                  setAddressError(null);
                  if (!val.trim()) {
                    setValue('formatted_address', '');
                    setValue('latitude', null);
                    setValue('longitude', null);
                  }
                }}
                onAddressSelect={handleAddressSelect}
                onManualEntry={() => setIsManualEntry(true)}
              />

              <Controller
                control={control}
                name="address_line_2"
                render={({ field }) => (
                  <Input
                    label={
                      <span>
                        Address line 2 <span className="text-grayscale-500">Optional</span>
                      </span>
                    }
                    placeholder="Apt / Suite / Floor"
                    {...field}
                  />
                )}
              />
            </>
          )}

          <div>
            <Button type="submit" variant="primary" disabled={!hasRequiredFields} isLoading={isCreating}>
              Add office
            </Button>
          </div>
        </form>

        <div
          className={cn(
            'mt-2 rounded-md',
            offices.length === 0 && 'border border-solid border-border-gray',
            offices.length > 0 && 'max-h-[160px] overflow-y-auto',
          )}
        >
          {isLoadingOffices ? (
            <LoadingSpinner height="64px" />
          ) : offices.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
                No offices have been added
              </Typography>
            </div>
          ) : (
            <InteractiveList
              height="auto"
              items={offices.map((office) => ({
                id: office.id,
                content: (
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Typography styledAs="bodyDMSans" weight={600}>
                        {office.name}
                      </Typography>
                      {office.tin && (
                        <Typography styledAs="bodyDMSans" color="var(--grayscale-500)">
                          TIN: {office.tin}
                        </Typography>
                      )}
                    </div>
                    <Typography styledAs="bodyDMSans" color="var(--text-gray)">
                      {office.address}
                    </Typography>
                  </div>
                ),
              }))}
              onRemoveItem={(id) => deleteOffice(id)}
            />
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddOfficeModal;
